/**
 * Add one master element to the cycle (f11.2). On success the grid and the
 * candidate list are invalidated -- the new MANUAL line arrives from the
 * server with its recalculation pending, which starts the grid's polling.
 * On `409 ELEMENT_ALREADY_IN_CYCLE` the candidate list is refetched so the
 * element disappears from it.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ApiError, api } from '@/api/client';
import type { AddForecastElementBody } from '@/api/client';
import { queryKeys } from '@/api/queryKeys';

export const ELEMENT_ALREADY_IN_CYCLE = 'ELEMENT_ALREADY_IN_CYCLE';

export function useAddElement(cycleId: string, businessUnitId: string) {
  const client = useQueryClient();
  const candidatesPrefix = ['cycle', cycleId, 'forecast-elements', businessUnitId] as const;

  return useMutation({
    mutationFn: (body: AddForecastElementBody) => api.addForecastElement(cycleId, body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.cycle.forecastGrid(cycleId, businessUnitId) });
      void client.invalidateQueries({ queryKey: queryKeys.cycle.forecastUnits(cycleId) });
      void client.invalidateQueries({ queryKey: candidatesPrefix });
    },
    onError: (error) => {
      if (error instanceof ApiError && error.reason === ELEMENT_ALREADY_IN_CYCLE) {
        void client.invalidateQueries({ queryKey: candidatesPrefix });
      }
    },
  });
}
